import React, { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { AnimatedHeader } from '../animations/AnimatedHeader'

export const AboutSection: React.FC = () => {
  const { t } = useTranslation()
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: false, amount: 0.2 })

  // Parallax effect for background elements
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  })

  const parallaxY1 = useTransform(scrollYProgress, [0, 1], [100, -100])
  const parallaxY2 = useTransform(scrollYProgress, [0, 1], [-80, 80])

  // Detect prefers-reduced-motion
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  // Animation variants for stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  }

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0.0, 0.2, 1]
      }
    }
  }

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: '#4A90E2',
      bgColor: 'rgba(74, 144, 226, 0.08)',
      borderColor: 'rgba(74, 144, 226, 0.2)',
      title: t('about.features.precision.title'),
      description: t('about.features.precision.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: '#9B59B6',
      bgColor: 'rgba(155, 89, 182, 0.08)',
      borderColor: 'rgba(155, 89, 182, 0.2)',
      title: t('about.features.speed.title'),
      description: t('about.features.speed.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      color: '#2ECC71',
      bgColor: 'rgba(46, 204, 113, 0.08)',
      borderColor: 'rgba(46, 204, 113, 0.2)',
      title: t('about.features.security.title'),
      description: t('about.features.security.description')
    }
  ]

  return (
    <section 
      id="about" 
      ref={sectionRef}
      className="relative z-30 py-32 overflow-hidden"
      style={{ backgroundColor: '#F7F5F5' }}
    >
      {/* Parallax Background Elements */}
      <motion.div
        className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ 
          background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
          y: prefersReducedMotion.current ? 0 : parallaxY1,
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ 
          background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
          y: prefersReducedMotion.current ? 0 : parallaxY2,
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <AnimatedHeader>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl mb-6"
              style={{ 
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
            >
              {t('about.title')}
            </h2>
          </AnimatedHeader>
          <AnimatedHeader delay={0.2}>
            <p 
              className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed"
              style={{ 
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              {t('about.subtitle')}
            </p>
          </AnimatedHeader>
        </div>

        {/* Feature Cards with Stagger Animation - Responsive Grid */}
        <motion.div
          className="grid gap-8"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          }}
          variants={prefersReducedMotion.current ? {} : containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={prefersReducedMotion.current ? {} : cardVariants}
              className="group rounded-3xl border transition-all duration-400"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(12px)',
                borderColor: feature.borderColor,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                willChange: prefersReducedMotion.current ? 'auto' : 'transform',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
              }}
              whileHover={
                prefersReducedMotion.current
                  ? {}
                  : {
                      y: -8,
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
                      transition: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }
                    }
              }
            >
              {/* Icon Container - Responsive sizing */}
              <motion.div
                className="rounded-2xl mb-6 flex items-center justify-center"
                style={{
                  backgroundColor: feature.bgColor,
                  color: feature.color,
                  width: 'clamp(3rem, 10vw, 4rem)',
                  height: 'clamp(3rem, 10vw, 4rem)',
                  minWidth: '44px',
                  minHeight: '44px',
                }}
                whileHover={
                  prefersReducedMotion.current
                    ? {}
                    : {
                        scale: 1.1,
                        rotate: 5,
                        transition: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }
                      }
                }
              >
                {feature.icon}
              </motion.div>

              {/* Title - Responsive sizing */}
              <h3 
                className="mb-4"
                style={{ 
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
                }}
              >
                {feature.title}
              </h3>

              {/* Description - Responsive sizing */}
              <p 
                className="leading-relaxed"
                style={{ 
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 500,
                  fontSize: 'clamp(0.938rem, 3vw, 1rem)',
                }}
              >
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
